import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getCronHealth, type HookSummary } from "@/lib/cron-health.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/admin/cron")({
  head: () => ({ meta: [{ title: "Cron Health — Admin" }] }),
  component: AdminCronPage,
});

function fmtAgo(iso: string | null) {
  if (!iso) return "never";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function AdminCronPage() {
  const navigate = useNavigate();
  const fetchHealth = useServerFn(getCronHealth);
  const checkAdmin = useServerFn(getIsAdmin);

  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState<HookSummary[]>([]);
  const [failures, setFailures] = useState<
    Array<{ hook_name: string; created_at: string; duration_ms: number | null; error: string | null }>
  >([]);

  const load = async () => {
    const r = await fetchHealth();
    if ("error" in r) return;
    setSummaries(r.summaries);
    setFailures(r.recent_failures);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await load();
      const t = window.setInterval(load, 30_000);
      return () => window.clearInterval(t);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Admin</p>
        <h1 className="mt-3 font-display text-3xl">Cron Health</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Last run, duration, and 24h failure counts for every scheduled hook.
          Refreshes automatically every 30 seconds.
        </p>

        {loading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <ul className="mt-8 space-y-3">
              {summaries.map((h) => {
                const tone =
                  h.last_ok === false || h.overdue
                    ? "border-destructive/50"
                    : h.last_ok
                      ? "border-emerald-400/40"
                      : "border-amber-400/40";
                return (
                  <li key={h.name} className={`border ${tone} bg-card p-4`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg">{h.label}</p>
                        <p className="text-[10px] tracking-wide-luxury text-muted-foreground">
                          /api/public/hooks/{h.name}
                        </p>
                      </div>
                      <div className="text-right text-xs">
                        <p>
                          {h.last_ok === null
                            ? "No runs in 24h"
                            : h.last_ok
                              ? "OK"
                              : "Failing"}
                          {h.overdue ? " · overdue" : ""}
                        </p>
                        <p className="text-muted-foreground">
                          Last run {fmtAgo(h.last_run_at)}
                          {h.last_duration_ms != null ? ` · ${h.last_duration_ms}ms` : ""}
                        </p>
                        <p className="text-muted-foreground">
                          {h.runs_24h} runs · {h.failures_24h} failures · expected every ~
                          {h.expected_period_min < 60
                            ? `${h.expected_period_min}m`
                            : `${Math.round(h.expected_period_min / 60)}h`}
                        </p>
                      </div>
                    </div>
                    {h.last_error && (
                      <p className="mt-3 text-xs text-destructive break-all">
                        {h.last_error}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>

            <h2 className="mt-12 font-display text-2xl">Recent failures</h2>
            {failures.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">
                No failed runs in the last 24 hours.
              </p>
            ) : (
              <ul className="mt-4 space-y-2">
                {failures.map((f, i) => (
                  <li key={i} className="border border-border bg-card p-3 text-xs">
                    <p className="font-mono">
                      {new Date(f.created_at).toLocaleString("en-GB")} · {f.hook_name}
                      {f.duration_ms != null ? ` · ${f.duration_ms}ms` : ""}
                    </p>
                    <p className="mt-1 text-destructive break-all">{f.error}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
}
